/**
 * Product Files Search Component
 * Allows users to search for products and view their support files
 */

if (typeof customElements !== 'undefined') {
  class ProductFilesSearch extends HTMLElement {
    constructor() {
      super();
      
      // Get settings
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.showProductImages = this.dataset.showProductImages === 'true';
      
      // DOM elements
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('.reset__button');
      this.predictiveWrapper = this.querySelector('.product-files-predictive-search__wrapper');
      this.predictiveResults = this.querySelector('[data-predictive-results]');
      this.resultsElement = this.querySelector('#product-files-search-results');
      this.loadingElement = this.querySelector('.product-files-search__loading');
      this.filesDisplay = this.querySelector('[data-files-display]');
      this.clearButton = this.querySelector('.product-files-clear__button');
      this.clearContainer = this.querySelector('.product-files-clear');
      
      // State
      this.searchTimeout = null;
      this.abortController = null;
      this.selectedIndex = -1;
      this.allResults = [];
      this.currentProduct = null;
      this.hasMoreResults = false;
      this.lastSearchQuery = '';
      this.graphqlCursor = null;
      this.isLoadingMore = false;
      this.emptyBatchCount = 0; // Track consecutive empty batches to prevent infinite loops
      
      this.init();
    }

    init() {
      this.setupEventListeners();
      this.checkUrlParams();
    }

    setupEventListeners() {
      // Input events
      this.input.addEventListener('input', this.handleInput.bind(this));
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('keydown', this.handleKeyDown.bind(this));
      
      // Form events
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
      
      // Store reference to search button for later updates
      this.searchButton = this.form.querySelector('.search__button');
      if (this.searchButton) {
        this.searchButton.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.predictiveResults.hasAttribute('open') && this.allResults.length > 0) {
            this.selectProduct(this.allResults[0]);
          }
        });
      }
      
      this.resetButton.addEventListener('click', this.handleReset.bind(this));
      if (this.clearButton) {
        this.clearButton.addEventListener('click', this.handleReset.bind(this));
      }
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeResults();
        }
      });
      
      // Close dropdown when focus leaves the component
      this.addEventListener('focusout', (e) => {
        // Use setTimeout to allow the browser to update document.activeElement
        setTimeout(() => {
          // Don't close if we're currently loading more results
          if (this.isLoadingMore) {
            console.log('Focusout blocked: currently loading more results');
            return;
          }
          
          // Don't close if mouse is still hovering over the dropdown
          // This prevents closing when clicking scrollbar or other non-focusable areas
          if (this.predictiveWrapper.matches(':hover')) {
            console.log('Focusout blocked: mouse hovering over dropdown');
            return;
          }
          
          // Check if the new focus target is outside this component
          const activeElement = document.activeElement;
          const isContained = this.contains(activeElement);
          console.log(`Focusout check: activeElement=${activeElement?.tagName}.${activeElement?.className}, isContained=${isContained}`);
          
          if (!isContained) {
            console.log('Closing dropdown - focus left component');
            this.closeResults();
          }
        }, 0);
      });
      
      // Prevent clicks within the dropdown from closing it
      this.predictiveWrapper.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (this.predictiveResults.hasAttribute('open') && !this.contains(e.target)) {
          this.closeResults();
        }
      });
      
      // Close dropdown on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.predictiveResults.hasAttribute('open')) {
          this.closeResults();
          this.input.focus();
        }
      });
    }

    /**
     * Check if URL has a product parameter and load it
     */
    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const productHandle = urlParams.get('product');
      
      console.log('checkUrlParams: productHandle =', productHandle);
      
      if (productHandle) {
        this.loadProductByHandle(productHandle);
      }
    }

    /**
     * Handle input changes
     */
    handleInput(e) {
      const value = e.target.value.trim();
      
      if (value.length > 0) {
        this.resetButton.classList.remove('hidden');
      } else {
        this.resetButton.classList.add('hidden');
        this.closeResults();
        return;
      }

      clearTimeout(this.searchTimeout);

      if (value.length < 2) {
        this.closeResults();
        return;
      }

      this.searchTimeout = setTimeout(() => {
        this.performSearch(value);
      }, 300);
    }

    /**
     * Handle focus on input
     */
    handleFocus() {
      const value = this.input.value.trim();
      // Only open dropdown if we have results AND they've been rendered in the DOM
      const hasRenderedResults = this.resultsElement.querySelectorAll('[role="option"]').length > 0;
      if (value.length >= 2 && this.allResults.length > 0 && hasRenderedResults) {
        this.openResults();
      }
    }

    /**
     * Handle form submission
     */
    handleFormSubmit(e) {
      e.preventDefault();
      // If dropdown is open with results, select the first one
      if (this.predictiveResults.hasAttribute('open') && this.allResults.length > 0) {
        this.selectProduct(this.allResults[0]);
      }
    }

    /**
     * Handle keyboard navigation
     */
    handleKeyDown(e) {
      const isDropdownOpen = this.predictiveResults.hasAttribute('open');
      
      if (!isDropdownOpen) return;

      const items = this.resultsElement.querySelectorAll('[role="option"]');
      
      if (items.length === 0) return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (this.selectedIndex < items.length - 1) {
            this.selectedIndex++;
            this.updateSelection(items);
            items[this.selectedIndex].focus();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (this.selectedIndex > 0) {
            this.selectedIndex--;
            this.updateSelection(items);
            items[this.selectedIndex].focus();
          } else if (this.selectedIndex === 0) {
            this.selectedIndex = -1;
            this.updateSelection(items);
            this.input.focus();
          }
          break;
        case 'Tab':
          // Don't intercept Tab at all from input - let natural tab order work
          // The focusout handler will manage dropdown state
          break;
        case 'Enter':
          e.preventDefault();
          if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
            this.selectProduct(this.allResults[this.selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          this.closeResults();
          this.input.focus();
          break;
      }
    }

    /**
     * Update selected item in dropdown
     */
    updateSelection(items) {
      items.forEach((item, index) => {
        if (index === this.selectedIndex) {
          item.setAttribute('aria-selected', 'true');
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.setAttribute('aria-selected', 'false');
        }
      });
    }

    /**
     * Handle reset button
     */
    handleReset() {
      this.input.value = '';
      this.resetButton.classList.add('hidden');
      this.closeResults();
      this.hideFilesDisplay();
      this.currentProduct = null;
      this.allResults = [];
      this.selectedIndex = -1;
      
      // Hide clear button
      if (this.clearContainer) {
        this.clearContainer.hidden = true;
      }
      
      this.input.focus();
      
      // Update URL
      const url = new URL(window.location);
      url.searchParams.delete('product');
      window.history.pushState({}, '', url);
    }

    /**
     * Perform search for products
     */
    async performSearch(query) {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.showLoading();

      try {
        let products = await this.searchProducts(query);
        
        // If no results and query ends with -CGL, try searching without it
        if (products.length === 0 && query.toUpperCase().endsWith('-CGL')) {
          const baseQuery = query.slice(0, -4); // Remove last 4 characters (-CGL)
          console.log(`No results for "${query}", trying base SKU: "${baseQuery}"`);
          products = await this.searchProducts(baseQuery);
          
          if (products.length > 0) {
            console.log(`Found ${products.length} products using base SKU`);
          }
        }
        
        this.allResults = products;
        this.displayPredictiveResults(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Product search error:', error);
        this.displayError('An error occurred while searching. Please try again.');
      } finally {
        this.hideLoading();
      }
    }

    /**
     * Extract clean search terms from query (removes filters and operators)
     */
    getSearchTerms(query) {
      return query
        .replace(/vendor:\S+/gi, '')
        .replace(/-tag:\S+/gi, '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(term => term.length > 0);
    }

    /**
     * Check if product title contains any of the search terms
     * Short terms (1-2 chars) require word boundaries to avoid false matches
     * Longer terms allow substring matching
     */
    titleContainsSearchTerms(product, searchTerms) {
      const title = product.title.toLowerCase();
      const sku = (product.sku || '').toLowerCase();
      // Check both cglSku (from enrichment) and cgl_sku (from GraphQL)
      const cglSku = (product.cglSku || product.cgl_sku || '').toLowerCase();
      
      // Check if any search term appears in title or SKU
      return searchTerms.some(term => {
        // For short terms (1-2 chars), require word boundary matching
        // This prevents "se" from matching "Cassegrain"
        if (term.length <= 2) {
          const wordBoundaryRegex = new RegExp(`\\b${this.escapeRegex(term)}\\b`, 'i');
          return wordBoundaryRegex.test(title) || 
                 wordBoundaryRegex.test(sku) || 
                 wordBoundaryRegex.test(cglSku);
        }
        
        // For longer terms, allow substring matching
        // This allows "nexstar" to match "NexStar 8SE"
        return title.includes(term) || 
               sku.includes(term) || 
               cglSku.includes(term);
      });
    }

    /**
     * Sort products by relevance to prioritize title/SKU matches over description matches
     * This helps counteract Shopify's broad search that includes descriptions
     */
    sortByRelevance(products, originalQuery) {
      // Remove vendor filter and tag filter from query for matching
      const query = originalQuery
        .replace(/vendor:\S+/gi, '')
        .replace(/-tag:\S+/gi, '')
        .trim()
        .toLowerCase();
      
      return products.sort((a, b) => {
        const scoreA = this.calculateRelevanceScore(a, query);
        const scoreB = this.calculateRelevanceScore(b, query);
        return scoreB - scoreA; // Higher score first
      });
    }
    
    /**
     * Calculate relevance score for a product based on query
     * Higher scores = better matches in title/SKU
     * Prioritizes titles that START with search terms
     */
    calculateRelevanceScore(product, query) {
      let score = 0;
      const title = product.title.toLowerCase();
      const sku = (product.sku || '').toLowerCase();
      // Check both cglSku (from enrichment) and cgl_sku (from GraphQL)
      const cglSku = (product.cglSku || product.cgl_sku || '').toLowerCase();
      const searchTerms = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
      const titleWords = title.split(/\s+/);
      const firstTitleWord = titleWords[0] || '';
      
      // === EXACT MATCHES (Highest Priority) ===
      
      // Exact title match
      if (title === query.toLowerCase()) {
        score += 10000;
      }
      
      // Exact SKU match
      if (sku === query.toLowerCase() || cglSku === query.toLowerCase()) {
        score += 8000;
      }
      
      // === TITLE STARTS WITH (Very High Priority) ===
      
      // Title starts with the complete query
      if (title.startsWith(query)) {
        score += 5000;
      }
      
      // Title starts with first search term (e.g., "NexStar" for "nexstar se")
      if (searchTerms.length > 0 && title.startsWith(searchTerms[0])) {
        score += 3000;
        
        // Bonus if subsequent terms also appear in order near the beginning
        let consecutiveMatches = 1;
        for (let i = 1; i < searchTerms.length; i++) {
          const position = title.indexOf(searchTerms[i]);
          if (position !== -1 && position < 50) { // Within first 50 chars
            consecutiveMatches++;
            score += 500;
          }
        }
        
        // Extra bonus if all terms found in order
        if (consecutiveMatches === searchTerms.length) {
          score += 1000;
        }
      }
      
      // First word of title matches first search term exactly
      // (e.g., "NexStar" for search "nexstar")
      if (searchTerms.length > 0 && firstTitleWord === searchTerms[0]) {
        score += 2000;
      }
      
      // Title starts with any of the search terms (not just first)
      for (let i = 1; i < searchTerms.length; i++) {
        if (title.startsWith(searchTerms[i])) {
          score += 1500;
          break;
        }
      }
      
      // === WHOLE WORD MATCHES (High Priority) ===
      
      // Count how many search terms appear as whole words
      let wholeWordMatches = 0;
      let totalWordScore = 0;
      
      searchTerms.forEach((term, index) => {
        const wordBoundaryRegex = new RegExp(`\\b${this.escapeRegex(term)}\\b`, 'i');
        if (wordBoundaryRegex.test(title)) {
          wholeWordMatches++;
          
          // Find position of this term in title
          const position = title.search(wordBoundaryRegex);
          
          // Earlier in title = higher score
          const positionScore = Math.max(0, 500 - position * 5);
          totalWordScore += positionScore;
          
          // First search term found = bonus
          if (index === 0) {
            totalWordScore += 300;
          }
        }
      });
      
      score += totalWordScore;
      
      // Bonus for having all search terms present as whole words
      if (wholeWordMatches === searchTerms.length) {
        score += 800;
      }
      
      // === SUBSTRING MATCHES (Medium Priority) ===
      
      // Title contains all search terms (even as substrings)
      const allTermsPresent = searchTerms.every(term => title.includes(term));
      if (allTermsPresent) {
        score += 300;
      }
      
      // Count substring matches
      const substringMatches = searchTerms.filter(term => title.includes(term)).length;
      score += substringMatches * 100;
      
      // === SKU MATCHES ===
      
      // SKU starts with query
      if (sku.startsWith(query) || cglSku.startsWith(query)) {
        score += 4000;
      }
      
      // SKU contains query
      else if (sku.includes(query) || cglSku.includes(query)) {
        score += 2000;
      }
      
      // SKU matches any search term
      else {
        searchTerms.forEach(term => {
          if (sku.includes(term) || cglSku.includes(term)) {
            score += 500;
          }
        });
      }
      
      // === BONUSES ===
      
      // Boost products that have support files
      if (product.supportFiles && product.supportFiles.length > 0) {
        score += 50;
      }
      
      return score;
    }
    
    /**
     * Escape special regex characters
     */
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Search products using GraphQL Storefront API
     * Note: GraphQL does NOT support Search & Discovery synonyms
     * This is used as a fallback when predictive search fails
     */
    async searchProductsGraphQL(query) {
      if (!this.storefrontToken || this.storefrontToken.trim() === '') {
        console.warn('No Storefront API token available for GraphQL search');
        return [];
      }

      // Add vendor filter and exclude used items and spare parts
      let searchQuery = query;
      if (!query.toLowerCase().includes('vendor:')) {
        searchQuery = `${query} vendor:Celestron`;
      }
      // Exclude products tagged with "used-item" or "sparepart"
      if (!query.toLowerCase().includes('tag:')) {
        searchQuery = `${searchQuery} -tag:used-item -tag:sparepart`;
      }
      
      console.log(`GraphQL search query: "${searchQuery}" (fallback - no synonyms)`);

      const graphqlQuery = `
        query searchProducts($query: String!) {
          products(first: 50, query: $query, sortKey: RELEVANCE) {
            edges {
              node {
                id
                title
                handle
                vendor
                featuredImage {
                  url
                  altText
                }
                variants(first: 1) {
                  edges {
                    node {
                      sku
                    }
                  }
                }
                metafield_sku: metafield(namespace: "cgl", key: "sku") {
                  value
                }
                supportFiles: metafield(namespace: "custom", key: "support_files") {
                  references(first: 100) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields {
                            key
                            value
                            reference {
                              ... on MediaImage {
                                image {
                                  url
                                }
                              }
                              ... on GenericFile {
                                url
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(window.Shopify.routes.root + 'api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken.trim()
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: { query: searchQuery }
        }),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error('GraphQL query failed');
      }

      // Parse products and filter by exact vendor match (case-sensitive)
      const allProducts = this.parseGraphQLProducts(result.data.products.edges);
      const vendorFiltered = allProducts.filter(product => product.vendor === 'Celestron');
      
      // For GraphQL search, trust GraphQL's relevance more
      // We already specified the search terms in the query, so GraphQL returns relevant products
      // Only do minimal title filtering for very specific cases (single-word searches or SKU searches)
      const searchTerms = this.getSearchTerms(searchQuery);
      const isSingleTerm = searchTerms.length === 1;
      const isHandleSearch = query.toLowerCase().includes('handle:');
      let filteredProducts = vendorFiltered;
      let titleFilterCount = 0;
      
      // Only apply strict title filtering for single-term searches to reduce noise
      // Multi-term searches can include accessories and related products
      if (!isHandleSearch && isSingleTerm && searchTerms[0].length > 3) {
        filteredProducts = vendorFiltered.filter(product => 
          this.titleContainsSearchTerms(product, searchTerms)
        );
        titleFilterCount = vendorFiltered.length - filteredProducts.length;
      } else {
        // For multi-term searches or short terms, trust GraphQL's relevance
        filteredProducts = vendorFiltered;
      }
      
      // Log filtering for debugging
      const vendorFilterCount = allProducts.length - vendorFiltered.length;
      
      if (vendorFilterCount > 0) {
        console.log(`Filtered out ${vendorFilterCount} products (non-Celestron, used items, or spare parts)`);
      }
      if (titleFilterCount > 0) {
        console.log(`Filtered out ${titleFilterCount} products (title doesn't contain search terms)`);
      }
      
      console.log(`Found ${filteredProducts.length} Celestron product(s) ${isHandleSearch ? '(handle search)' : 'with matching titles/SKUs'} (GraphQL)`);
      
      // Re-sort by relevance to prioritize title/SKU matches over description matches
      const sortedProducts = this.sortByRelevance(filteredProducts, searchQuery);
      
      // Limit to top 12 results for display (we fetched 50 to ensure relevant products make it through filtering)
      const displayProducts = sortedProducts.slice(0, 12);
      
      if (displayProducts.length > 0) {
        console.log(`Showing top ${displayProducts.length} of ${sortedProducts.length} GraphQL results:`);
        displayProducts.forEach((p, i) => {
          console.log(`  ${i + 1}. ${p.title} (${p.handle}) - ${p.supportFiles?.length || 0} files`);
        });
      }
      
      return displayProducts;
    }

    /**
     * Primary search using Shopify's predictive search API
     * This method supports Search & Discovery synonyms
     * After getting results, enriches them with metafields via GraphQL
     */
    async searchProducts(query, append = false) {
      console.log(`Predictive search query: "${query}" (append: ${append})`);
      
      // Store query for load more functionality
      this.lastSearchQuery = query;
      
      // If appending (Load More clicked), always use GraphQL with cursor
      if (append) {
        return await this.searchProductsGraphQLWithCursor(query, this.graphqlCursor);
      }
      
      // Reset pagination state for new searches
      this.graphqlCursor = null;
      this.hasMoreResults = false;
      this.emptyBatchCount = 0;
      
      // Detect if this looks like a SKU search
      // SKUs typically: contain numbers, no spaces, may have hyphens, shorter length
      // Examples: "12026", "11069-CGL", "93230"
      const isSKULike = /^[A-Z0-9\-]+$/i.test(query.trim()) && 
                        /\d/.test(query) && // Must contain at least one number
                        !query.includes(' ') && 
                        query.length <= 15; // SKUs are typically shorter
      
      // If it looks like a SKU and we have GraphQL access, use GraphQL for better SKU matching
      if (isSKULike && this.storefrontToken && this.storefrontToken.trim() !== '') {
        console.log('Query looks like SKU, using GraphQL for better SKU matching');
        return await this.searchProductsGraphQL(query);
      }
      
      // Explicitly set searchable fields to exclude body (product description)
      // Search only: title, product_type, variants.sku, variants.title, vendor
      // Request more products (50) since we filter heavily after
      const searchFields = 'title,product_type,variants.sku,variants.title,vendor';
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=50&resources[limit_scope]=each&resources[options][fields]=${searchFields}`;
      
      const response = await fetch(searchUrl, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      const allProducts = data.resources?.results?.products || [];
      
      console.log(`Predictive API returned ${allProducts.length} products`);
      
      // Filter by vendor Celestron (exact match, case-sensitive) and exclude used items and spare parts
      const vendorFiltered = allProducts
        .filter(product => {
          // Must be Celestron vendor
          if (product.vendor !== 'Celestron') return false;
          
          // Exclude if tagged with "used-item" or "sparepart"
          const tags = product.tags || [];
          if (tags.includes('used-item') || tags.includes('sparepart')) return false;
          
          return true;
        })
        .map(product => ({
          id: product.id,
          title: product.title,
          handle: product.handle,
          vendor: product.vendor,
          image: product.image,
          sku: product.variants?.[0]?.sku || '',
          cglSku: null,
          supportFiles: []
        }));
      
      // Apply smart filtering to balance synonym matching with noise reduction
      // Strategy:
      // 1. Always keep top-ranked results (likely synonym matches or highly relevant)
      // 2. Apply strict title filtering to lower-ranked results to remove noise
      // 3. For multi-term searches, be more lenient (related products are expected)
      const searchTerms = this.getSearchTerms(query);
      const topResultsToKeep = 8; // Keep top 8 results regardless (synonym matches rank high)
      const isMultiTermSearch = searchTerms.length > 1;
      
      const filteredProducts = vendorFiltered.filter((product, index) => {
        // Always keep top results - these are highly ranked by Shopify
        // and likely include synonym matches
        if (index < topResultsToKeep) {
          return true;
        }
        
        // For multi-term searches, be more lenient with lower results
        // (accessories and related products are expected)
        if (isMultiTermSearch) {
          return this.titleContainsSearchTerms(product, searchTerms);
        }
        
        // For single-term searches, apply strict filtering to lower results
        // to remove noise
        return this.titleContainsSearchTerms(product, searchTerms);
      });
      
      // Log filtering for debugging
      const vendorFilterCount = allProducts.length - vendorFiltered.length;
      const titleFilterCount = vendorFiltered.length - filteredProducts.length;
      const topResultsKept = Math.min(topResultsToKeep, vendorFiltered.length);
      
      if (vendorFilterCount > 0) {
        console.log(`Filtered out ${vendorFilterCount} products (non-Celestron, used items, or spare parts) - predictive search`);
      }
      if (titleFilterCount > 0) {
        console.log(`Filtered out ${titleFilterCount} products (title doesn't contain search terms) - predictive search`);
        console.log(`Kept top ${topResultsKept} results regardless (preserves synonym matches)`);
      }
      
      // Re-sort by relevance to prioritize title/SKU matches
      const sortedProducts = this.sortByRelevance(filteredProducts, query);
      
      // Limit to top 12 results for display (we fetched 50 to ensure relevant products make it through filtering)
      const displayProducts = sortedProducts.slice(0, 12);
      
      console.log(`Found ${sortedProducts.length} Celestron product(s) (predictive search)`);
      if (displayProducts.length > 0) {
        console.log(`Showing top ${displayProducts.length} of ${sortedProducts.length} results:`);
        displayProducts.forEach((p, i) => {
          console.log(`  ${i + 1}. ${p.title} (${p.handle})`);
        });
      }
      
      // Enrich products with metafields (support files) via GraphQL
      if (displayProducts.length > 0) {
        await this.enrichProductsWithMetafields(displayProducts);
      }
      
      // Check if we might have more results via GraphQL
      // Show "Load More" if:
      // 1. We got a significant number of results (3+) - GraphQL might have more that Predictive API missed
      // 2. Predictive API returned exactly 50 (its limit) - likely more exist
      // 3. We cut off sorted results (sorted > display)
      // 4. We have a Storefront token available for GraphQL
      const hasSignificantResults = displayProducts.length >= 3;
      const atPredictiveLimit = allProducts.length === 50; // Predictive API caps at 50
      const hasCutoffResults = sortedProducts.length > displayProducts.length;
      const hasGraphQLAccess = this.storefrontToken && this.storefrontToken.trim() !== '';
      
      if (hasGraphQLAccess && (hasSignificantResults || atPredictiveLimit || hasCutoffResults)) {
        this.hasMoreResults = true;
        // Initialize cursor to null for first GraphQL pagination request
        if (!this.graphqlCursor) {
          this.graphqlCursor = null;
        }
        console.log(`More results available: allProducts=${allProducts.length}, sorted=${sortedProducts.length}, display=${displayProducts.length}, reason: ${hasSignificantResults ? 'significant results' : ''} ${atPredictiveLimit ? 'at predictive limit' : ''} ${hasCutoffResults ? 'cutoff results' : ''}`);
      } else {
        this.hasMoreResults = false;
        console.log(`No more results: allProducts=${allProducts.length}, sorted=${sortedProducts.length}, display=${displayProducts.length}, hasGraphQL=${hasGraphQLAccess}`);
      }
      
      return displayProducts;
    }

    /**
     * Load more results using GraphQL with pagination
     * This is called when the "Load More" button is clicked
     */
    async searchProductsGraphQLWithCursor(query, cursor) {
      if (!this.storefrontToken || this.storefrontToken.trim() === '') {
        console.warn('No Storefront API token available for load more');
        return [];
      }

      // Add vendor filter and exclude used items and spare parts
      let searchQuery = query;
      if (!query.toLowerCase().includes('vendor:')) {
        searchQuery = `${query} vendor:Celestron`;
      }
      if (!query.toLowerCase().includes('tag:')) {
        searchQuery = `${searchQuery} -tag:used-item -tag:sparepart`;
      }
      
      console.log(`GraphQL pagination query: "${searchQuery}" (cursor: ${cursor ? 'yes' : 'initial'})`);

      const graphqlQuery = `
        query searchProducts($query: String!, $cursor: String) {
          products(first: 50, query: $query, sortKey: RELEVANCE, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                title
                handle
                vendor
                featuredImage {
                  url
                  altText
                }
                variants(first: 1) {
                  edges {
                    node {
                      sku
                    }
                  }
                }
                metafield_sku: metafield(namespace: "cgl", key: "sku") {
                  value
                }
                supportFiles: metafield(namespace: "custom", key: "support_files") {
                  references(first: 100) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields {
                            key
                            value
                            reference {
                              ... on MediaImage {
                                image {
                                  url
                                }
                              }
                              ... on GenericFile {
                                url
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(window.Shopify.routes.root + 'api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken.trim()
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: { 
            query: searchQuery,
            cursor: cursor
          }
        }),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error('GraphQL query failed');
      }

      // Parse products and filter
      const allProducts = this.parseGraphQLProducts(result.data.products.edges);
      const vendorFiltered = allProducts.filter(product => product.vendor === 'Celestron');
      
      // For GraphQL pagination, trust GraphQL's relevance more
      // We already specified the search terms in the query, so GraphQL returns relevant products
      // Only do minimal title filtering for very specific cases (single-word searches or SKU searches)
      const searchTerms = this.getSearchTerms(searchQuery);
      const isSingleTerm = searchTerms.length === 1;
      const isHandleSearch = query.toLowerCase().includes('handle:');
      let filteredProducts = vendorFiltered;
      let titleFilterCount = 0;
      
      // Only apply strict title filtering for single-term searches to reduce noise
      // Multi-term searches can include accessories and related products
      if (!isHandleSearch && isSingleTerm && searchTerms[0].length > 3) {
        filteredProducts = vendorFiltered.filter(product => 
          this.titleContainsSearchTerms(product, searchTerms)
        );
        titleFilterCount = vendorFiltered.length - filteredProducts.length;
      } else {
        // For multi-term searches or short terms, trust GraphQL's relevance
        filteredProducts = vendorFiltered;
      }
      
      // Log filtering
      const vendorFilterCount = allProducts.length - vendorFiltered.length;
      
      if (vendorFilterCount > 0) {
        console.log(`Filtered out ${vendorFilterCount} products (non-Celestron, used items, or spare parts) - GraphQL pagination`);
      }
      if (titleFilterCount > 0) {
        console.log(`Filtered out ${titleFilterCount} products (title doesn't contain search terms) - GraphQL pagination`);
      }
      
      console.log(`Found ${filteredProducts.length} additional Celestron product(s) (GraphQL pagination)`);
      
      // Re-sort by relevance
      const sortedProducts = this.sortByRelevance(filteredProducts, searchQuery);
      
      // Update pagination state based on GraphQL response
      const pageInfo = result.data.products.pageInfo;
      
      // For pagination, return all sorted products instead of slicing to 12
      // This way, if we found 33 products, all 33 will be added to the results
      // The display layer will show them all at once rather than requiring multiple clicks
      const resultsToReturn = sortedProducts;
      
      // Set hasMoreResults only if GraphQL has more pages to fetch
      if (pageInfo.hasNextPage && resultsToReturn.length > 0) {
        this.graphqlCursor = pageInfo.endCursor;
        this.hasMoreResults = true;
        console.log(`More pages available in GraphQL (cursor set)`);
      } else if (pageInfo.hasNextPage && resultsToReturn.length === 0) {
        // There are more pages but we got filtered down to 0 results
        // Keep cursor and hasMoreResults for another attempt
        this.graphqlCursor = pageInfo.endCursor;
        this.hasMoreResults = true;
        console.log('No results after filtering, but more pages available - will try next batch');
      } else {
        this.graphqlCursor = null;
        this.hasMoreResults = false;
      }
      
      console.log(`Pagination: hasMore=${this.hasMoreResults}, cursor=${this.graphqlCursor ? 'yes' : 'no'}, returning ${resultsToReturn.length} of ${sortedProducts.length} products`);
      
      // Return all sorted products (up to 50 from this GraphQL batch)
      return resultsToReturn;
    }

    /**
     * Enrich products with metafields (support_files, cgl.sku) via GraphQL
     * This is called after predictive search to add metafield data
     */
    async enrichProductsWithMetafields(products) {
      if (!this.storefrontToken || this.storefrontToken.trim() === '' || products.length === 0) {
        return;
      }
      
      console.log(`Enriching ${products.length} products with metafields via GraphQL...`);
      
      // Build query to fetch multiple products by handle
      const handles = products.map(p => p.handle);
      const handleQueries = handles.map((handle, i) => `
        product${i}: product(handle: "${handle}") {
          handle
          variants(first: 1) {
            edges {
              node {
                sku
              }
            }
          }
          metafield_sku: metafield(namespace: "cgl", key: "sku") {
            value
          }
          supportFiles: metafield(namespace: "custom", key: "support_files") {
            references(first: 100) {
              edges {
                node {
                  ... on Metaobject {
                    id
                    handle
                    fields {
                      key
                      value
                      reference {
                        ... on MediaImage {
                          image {
                            url
                          }
                        }
                        ... on GenericFile {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `).join('\n');
      
      const graphqlQuery = `
        query enrichProducts {
          ${handleQueries}
        }
      `;
      
      try {
        const response = await fetch(window.Shopify.routes.root + 'api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken.trim()
          },
          body: JSON.stringify({ query: graphqlQuery }),
          signal: this.abortController.signal
        });

        if (!response.ok) {
          console.warn('Failed to enrich products with metafields');
          return;
        }

        const result = await response.json();
        
        if (result.errors) {
          console.warn('GraphQL errors when enriching products:', result.errors);
          return;
        }
        
        // Merge metafield data into products
        products.forEach((product, i) => {
          const enrichedData = result.data[`product${i}`];
          if (enrichedData) {
            // Add regular SKU if not already present (Predictive API doesn't return it)
            const variantSku = enrichedData.variants?.edges?.[0]?.node?.sku;
            if (variantSku && !product.sku) {
              product.sku = variantSku;
            }
            
            // Add cgl.sku if available
            if (enrichedData.metafield_sku?.value) {
              product.cglSku = enrichedData.metafield_sku.value;
            }
            
            // Add support files
            if (enrichedData.supportFiles?.references?.edges) {
              product.supportFiles = enrichedData.supportFiles.references.edges
                .map(edge => this.parseMetaobject(edge.node))
                .filter(file => file !== null);
            }
          }
        });
        
        console.log(`Successfully enriched products with metafields`);
      } catch (error) {
        console.warn('Error enriching products with metafields:', error);
        // Don't throw - products will just not have support files data
      }
    }

    /**
     * Parse GraphQL product data
     */
    parseGraphQLProducts(edges) {
      return edges.map(edge => {
        const node = edge.node;
        const sku = node.variants?.edges?.[0]?.node?.sku || '';
        const cglSku = node.metafield_sku?.value || null;
        
        // Parse support files
        const supportFiles = [];
        if (node.supportFiles?.references?.edges) {
          node.supportFiles.references.edges.forEach(fileEdge => {
            const fileNode = fileEdge.node;
            const file = this.parseMetaobject(fileNode);
            if (file) {
              supportFiles.push(file);
            }
          });
        }

        return {
          id: node.id,
          title: node.title,
          handle: node.handle,
          vendor: node.vendor,
          image: node.featuredImage?.url || null,
          sku: sku,
          cgl_sku: cglSku,
          supportFiles: supportFiles
        };
      });
    }

    /**
     * Parse metaobject fields into a structured object
     */
    parseMetaobject(metaobject) {
      const file = {
        id: metaobject.id,
        handle: metaobject.handle
      };

      metaobject.fields.forEach(field => {
        switch (field.key) {
          case 'file_title':
            file.title = field.value;
            break;
          case 'category':
            file.category = field.value;
            break;
          case 'file_description':
            file.description = field.value;
            break;
          case 'file_url_remote':
            file.remoteUrl = field.value;
            break;
          case 'file_shopify':
            if (field.reference) {
              file.shopifyFile = field.reference.url || field.reference.image?.url;
            }
            break;
          case 'page_blog_uri':
            file.pageUri = field.value;
            break;
        }
      });

      return file;
    }

    /**
     * Get a single product by its exact handle using GraphQL
     * This is more reliable than search queries for handle-based lookups
     */
    async getProductByHandle(handle) {
      if (!this.storefrontToken || this.storefrontToken.trim() === '') {
        console.warn('No Storefront API token available');
        return null;
      }

      const graphqlQuery = `
        query getProduct($handle: String!) {
          product(handle: $handle) {
            id
            title
            handle
            vendor
            featuredImage {
              url
              altText
            }
            variants(first: 1) {
              edges {
                node {
                  sku
                }
              }
            }
            metafield_sku: metafield(namespace: "cgl", key: "sku") {
              value
            }
            supportFiles: metafield(namespace: "custom", key: "support_files") {
              references(first: 100) {
                edges {
                  node {
                    ... on Metaobject {
                      id
                      handle
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image {
                              url
                            }
                          }
                          ... on GenericFile {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(window.Shopify.routes.root + 'api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken.trim()
          },
          body: JSON.stringify({
            query: graphqlQuery,
            variables: { handle: handle }
          }),
          signal: this.abortController.signal
        });

        if (!response.ok) {
          console.error(`GraphQL request failed: ${response.status}`);
          return null;
        }

        const result = await response.json();

        if (result.errors) {
          console.error('GraphQL errors:', result.errors);
          return null;
        }

        const productData = result.data?.product;
        if (!productData) {
          return null;
        }

        // Check vendor
        if (productData.vendor !== 'Celestron') {
          console.log(`Product found but vendor is "${productData.vendor}", not "Celestron"`);
          return null;
        }

        // Parse into our product format
        const sku = productData.variants?.edges?.[0]?.node?.sku || '';
        const cglSku = productData.metafield_sku?.value || null;
        const supportFiles = productData.supportFiles?.references?.edges
          ? productData.supportFiles.references.edges
              .map(edge => this.parseMetaobject(edge.node))
              .filter(file => file !== null)
          : [];

        return {
          id: productData.id,
          title: productData.title,
          handle: productData.handle,
          vendor: productData.vendor,
          image: productData.featuredImage?.url || null,
          sku: sku,
          cglSku: cglSku,
          supportFiles: supportFiles
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('Error fetching product by handle:', error);
        return null;
      }
    }

    /**
     * Load a product by its handle (from URL parameter)
     */
    async loadProductByHandle(handle) {
      // Create a temporary abort controller for this operation
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      
      try {
        console.log(`Loading product from URL parameter: ${handle}`);
        
        // Use direct GraphQL product lookup by handle (not search query)
        let product = await this.getProductByHandle(handle);
        console.log(`getProductByHandle returned:`, product ? {title: product.title, handle: product.handle} : 'null');
        
        // If no results and handle ends with -cgl, try without it
        if (!product && handle.toLowerCase().endsWith('-cgl')) {
          const baseHandle = handle.slice(0, -4);
          console.log(`No product found for "${handle}", trying base handle: "${baseHandle}"`);
          product = await this.getProductByHandle(baseHandle);
          console.log(`Base handle returned:`, product ? {title: product.title, handle: product.handle} : 'null');
        }
        
        if (product) {
          console.log(`Product found: ${product.title} | handle: ${product.handle}`);
          // Populate the input field
          this.input.value = product.title;
          this.resetButton.classList.remove('hidden');
          
          // Store results and select the product
          this.allResults = [product];
          this.selectProduct(product);
          
          // Announce to screen reader
          this.announceToScreenReader(`Loaded ${product.title} from URL`);
        } else {
          console.warn(`No product found with handle: ${handle}`);
          this.input.placeholder = `Could not find product: ${handle}`;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error loading product from URL:', error);
        this.input.placeholder = 'Error loading product from URL';
      }
    }

    /**
     * Handle load more button click
     */
    async handleLoadMore() {
      if (this.isLoadingMore || !this.hasMoreResults || !this.lastSearchQuery) {
        return;
      }
      
      this.isLoadingMore = true;
      
      // Remember how many items we had before loading more (for focus management)
      const itemsBeforeLoad = this.resultsElement.querySelectorAll('[role="option"]').length;
      
      // Check if Load More button currently has focus (keyboard activation)
      const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
      const buttonHadFocus = loadMoreBtn && document.activeElement === loadMoreBtn;
      
      // Update button state
      if (loadMoreBtn) {
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'Loading...';
      }
      
      try {
        console.log('Loading more results...');
        
        // Fetch more results using GraphQL with cursor
        const moreProducts = await this.searchProducts(this.lastSearchQuery, true);
        
        if (moreProducts.length > 0) {
          // Filter out duplicates by handle
          const existingHandles = new Set(this.allResults.map(p => p.handle));
          const uniqueNewProducts = moreProducts.filter(p => !existingHandles.has(p.handle));
          
          console.log(`Fetched ${moreProducts.length} products, ${uniqueNewProducts.length} are unique`);
          
          if (uniqueNewProducts.length > 0) {
            // Reset empty batch counter on successful load
            this.emptyBatchCount = 0;
            
            // Append unique products to allResults
            this.allResults.push(...uniqueNewProducts);
            
            // Reset loading state BEFORE displaying results so the new Load More button renders correctly
            this.isLoadingMore = false;
            
            // Append to display
            console.log(`Before display: hasMoreResults=${this.hasMoreResults}, cursor=${this.graphqlCursor ? 'exists' : 'null'}`);
            this.displayPredictiveResults(uniqueNewProducts, this.lastSearchQuery, true);
            console.log(`After display: hasMoreResults=${this.hasMoreResults}, cursor=${this.graphqlCursor ? 'exists' : 'null'}`);
            
            // Use setTimeout to ensure DOM has updated before moving focus
            setTimeout(() => {
              // Always move focus to the first newly added item to ensure proper focus tracking
              // This is critical for both keyboard and mouse activation
              const allItems = this.resultsElement.querySelectorAll('[role="option"]');
              if (allItems[itemsBeforeLoad]) {
                allItems[itemsBeforeLoad].focus();
                console.log(`Focus moved to item ${itemsBeforeLoad} after Load More`);
              } else if (allItems.length > 0) {
                // Fallback to first item if index doesn't exist
                allItems[0].focus();
                console.log('Focus moved to first item after Load More (fallback)');
              }
              
              // Ensure dropdown is definitely open and input is expanded
              if (!this.predictiveResults.hasAttribute('open')) {
                this.predictiveResults.setAttribute('open', '');
                this.input.setAttribute('aria-expanded', 'true');
                console.log('Reopened dropdown after Load More');
              }
              
              // Verify Load More button state
              const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
              console.log(`Load More button in DOM after append: ${loadMoreBtn ? 'YES (enabled)' : 'NO'}, text: ${loadMoreBtn?.textContent}`);
            }, 50);
            
            this.announceToScreenReader(`${uniqueNewProducts.length} more products loaded. ${this.allResults.length} total results.`);
          } else {
            // All products were duplicates or filtered out
            this.emptyBatchCount++;
            
            // If there are still more pages available and we haven't hit the limit, try again automatically
            if (this.hasMoreResults && this.graphqlCursor && this.emptyBatchCount < 3) {
              console.log(`Got 0 unique products (attempt ${this.emptyBatchCount}/3), but more pages available - trying next batch automatically`);
              
              // Update button to show automatic loading
              const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
              if (loadMoreBtn) {
                loadMoreBtn.textContent = `Loading more (${this.emptyBatchCount}/3)...`;
              }
              
              this.isLoadingMore = false; // Reset to allow next call
              
              // Recursively try the next batch with a small delay to prevent blocking
              setTimeout(() => {
                this.handleLoadMore();
              }, 100);
            } else {
              if (this.emptyBatchCount >= 3) {
                console.log('Reached maximum empty batch attempts (3), stopping pagination');
              }
              this.hasMoreResults = false;
              this.emptyBatchCount = 0;
              this.announceToScreenReader('No more unique results available.');
              this.isLoadingMore = false;
              
              // Remove the Load More button since there are no more results
              const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
              if (loadMoreBtn) {
                loadMoreBtn.closest('.product-files-search__load-more')?.remove();
              }
            }
          }
        } else {
          // No products returned from GraphQL at all
          this.hasMoreResults = false;
          this.announceToScreenReader('No more results available.');
          this.isLoadingMore = false;
          
          // Remove the Load More button since there are no more results
          const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
          if (loadMoreBtn) {
            loadMoreBtn.closest('.product-files-search__load-more')?.remove();
            console.log('Removed Load More button - no more results from API');
          }
        }
      } catch (error) {
        console.error('Error loading more results:', error);
        this.announceToScreenReader('Error loading more results. Please try again.');
        this.isLoadingMore = false;
        
        // Reset button state on error
        const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
        if (loadMoreBtn) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = 'Load More Results';
        }
      }
    }

    /**
     * Display predictive search results
     */
    displayPredictiveResults(products, query, append = false) {
      if (!append) {
        this.resultsElement.innerHTML = '';
        this.selectedIndex = -1;
      }

      if (products.length === 0 && !append) {
        this.resultsElement.innerHTML = `
          <div class="product-files-search__empty">
            <p>No products found for "${this.escapeHtml(query)}"</p>
          </div>
        `;
        // Update search button label
        if (this.searchButton) {
          this.searchButton.setAttribute('aria-label', 'No results to select');
        }
        return;
      }
      
      // Update search button label with available results
      if (this.searchButton && !append) {
        this.searchButton.setAttribute('aria-label', `Select first result: ${products[0].title}`);
      }

      // If appending, we need to get existing list or create one
      let listElement = this.resultsElement.querySelector('.product-files-search__results-list');
      
      if (!listElement) {
        // Create new list
        const listHTML = `<ul class="product-files-search__results-list" role="listbox"></ul>`;
        this.resultsElement.innerHTML = listHTML;
        listElement = this.resultsElement.querySelector('.product-files-search__results-list');
      }
      
      // Remove existing load more button if present
      const existingLoadMore = listElement.querySelector('.product-files-search__load-more');
      if (existingLoadMore) {
        existingLoadMore.remove();
      }
      
      // Get current count of items for proper indexing
      const currentItemCount = this.allResults.length - products.length;
      
      // Append new product items
      const itemsHTML = products.map((product, index) => 
        this.createProductResultItem(product, currentItemCount + index)
      ).join('');
      
      listElement.insertAdjacentHTML('beforeend', itemsHTML);
      
      // Add load more button if there are more results
      console.log(`displayPredictiveResults: hasMoreResults=${this.hasMoreResults}, append=${append}`);
      if (this.hasMoreResults) {
        console.log('Adding Load More button to dropdown');
        const loadMoreHTML = `
          <li class="product-files-search__load-more">
            <button type="button" class="product-files-search__load-more-button" tabindex="-1" ${this.isLoadingMore ? 'disabled' : ''}>
              ${this.isLoadingMore ? 'Loading...' : 'Load More Results'}
            </button>
          </li>
        `;
        listElement.insertAdjacentHTML('beforeend', loadMoreHTML);
        
        // Add event handlers to load more button (use lastElementChild to get the just-added button)
        const loadMoreLi = listElement.lastElementChild;
        const loadMoreBtn = loadMoreLi?.querySelector('.product-files-search__load-more-button');
        if (loadMoreBtn) {
          console.log('Load More button handlers attached');
          
          // Click handler (for mouse and keyboard activation)
          loadMoreBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleLoadMore();
          });
          
          // Explicit keyboard handler
          loadMoreBtn.addEventListener('keydown', (e) => {
            switch (e.key) {
              case 'Enter':
              case ' ':
                e.preventDefault();
                this.handleLoadMore();
                break;
              case 'Escape':
                e.preventDefault();
                this.closeResults();
                this.input.focus();
                break;
              case 'ArrowUp':
                e.preventDefault();
                // Move to last product item
                const items = this.resultsElement.querySelectorAll('[role="option"]');
                if (items.length > 0) {
                  items[items.length - 1].focus();
                }
                break;
            }
          });
        } else {
          console.warn('Failed to attach handlers to Load More button');
        }
      } else {
        console.log('Not adding Load More button - no more results available');
      }
      
      // Open dropdown now that results are populated
      if (!append) {
        this.openResults();
        
        // Announce results now that they're displayed
        this.announceToScreenReader(`${products.length} product ${products.length === 1 ? 'suggestion' : 'suggestions'} available. Use arrow keys to navigate.`);
      } else {
        // For appended results, ensure dropdown stays open and make items focusable
        // The dropdown might have closed during the load, so reopen it
        if (!this.predictiveResults.hasAttribute('open')) {
          this.predictiveResults.setAttribute('open', '');
          this.input.setAttribute('aria-expanded', 'true');
        }
        
        const allItems = this.resultsElement.querySelectorAll('[role="option"]');
        allItems.forEach(item => {
          item.setAttribute('tabindex', '0');
        });
        
        // Also update Load More button if present
        const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
        if (loadMoreBtn) {
          loadMoreBtn.setAttribute('tabindex', '0');
        }
      }

      // Add click and keyboard handlers to newly added items only
      const newItems = Array.from(listElement.querySelectorAll('[role="option"]')).slice(-products.length);
      newItems.forEach((item, index) => {
        const productIndex = currentItemCount + index;
        const product = this.allResults[productIndex];
        
        // Click handler
        item.addEventListener('click', () => {
          this.selectProduct(product);
        });
        
        // Keyboard handler
        item.addEventListener('keydown', (e) => {
          const items = this.resultsElement.querySelectorAll('[role="option"]');
          
          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              this.selectProduct(product);
              break;
            case 'ArrowDown':
              e.preventDefault();
              if (productIndex < items.length - 1) {
                this.selectedIndex = productIndex + 1;
                this.updateSelection(items);
                items[this.selectedIndex].focus();
              }
              break;
            case 'ArrowUp':
              e.preventDefault();
              if (productIndex > 0) {
                this.selectedIndex = productIndex - 1;
                this.updateSelection(items);
                items[this.selectedIndex].focus();
              } else {
                this.selectedIndex = -1;
                this.updateSelection(items);
                this.input.focus();
              }
              break;
            case 'Escape':
              e.preventDefault();
              this.closeResults();
              this.input.focus();
              break;
            case 'Tab':
              // Don't prevent default - allow natural tab navigation
              // Focusout handler will close dropdown if focus leaves component
              break;
          }
        });
        
        // Update selectedIndex on focus
        item.addEventListener('focus', () => {
          this.selectedIndex = productIndex;
          this.updateSelection(this.resultsElement.querySelectorAll('[role="option"]'));
        });
      });

      this.announceToScreenReader(`${products.length} products found`);
    }

    /**
     * Create HTML for a product result item
     */
    createProductResultItem(product, index) {
      const filesCount = product.supportFiles?.length || 0;
      const imageHTML = this.showProductImages && product.image ? `
        <img 
          src="${product.image}" 
          alt="${this.escapeHtml(product.title)}"
          class="product-files-search__result-image"
          loading="lazy"
        >
      ` : '';

      // Support both cglSku (camelCase from enrichment) and cgl_sku (snake_case from GraphQL)
      const cglSkuValue = product.cglSku || product.cgl_sku;
      const skuDisplay = product.sku ? `SKU: ${this.escapeHtml(product.sku)}` : '';
      const cglSkuDisplay = cglSkuValue ? ` (UK: ${this.escapeHtml(cglSkuValue)})` : '';

      return `
        <li 
          class="product-files-search__result-item" 
          role="option"
          id="product-result-${index}"
          aria-selected="false"
          tabindex="-1"
        >
          <div class="product-files-search__result-content">
            ${imageHTML}
            <div class="product-files-search__result-info">
              <div class="product-files-search__result-title">
                ${this.escapeHtml(product.title)}
              </div>
              ${skuDisplay || cglSkuDisplay ? `
                <div class="product-files-search__result-sku">
                  ${skuDisplay}${cglSkuDisplay}
                </div>
              ` : ''}
              <div class="product-files-search__result-files-count">
                ${filesCount} ${filesCount === 1 ? 'file' : 'files'} available
              </div>
            </div>
          </div>
        </li>
      `;
    }

    /**
     * Select a product and display its files
     */
    selectProduct(product) {
      console.log('selectProduct called with:', product.title, '| handle:', product.handle);
      
      this.currentProduct = product;
      this.closeResults();
      this.displayProductFiles(product);
      
      // Show clear button
      if (this.clearContainer) {
        this.clearContainer.hidden = false;
      }
      
      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('product', product.handle);
      console.log('Setting URL to:', url.toString());
      window.history.pushState({}, '', url);
      
      this.announceToScreenReader(`Viewing files for ${product.title}`);
    }

    /**
     * Display product support files
     */
    displayProductFiles(product) {
      const filesCount = product.supportFiles?.length || 0;
      
      // Build SKU display - support both cglSku (camelCase) and cgl_sku (snake_case)
      const cglSkuValue = product.cglSku || product.cgl_sku;
      const skuParts = [];
      if (product.sku) skuParts.push(`SKU: ${product.sku}`);
      if (cglSkuValue) skuParts.push(`UK SKU: ${cglSkuValue}`);
      const skuDisplay = skuParts.length > 0 ? `<p class="product-files-display__product-sku">${skuParts.join(' • ')}</p>` : '';

      // Build the complete HTML structure
      let contentHTML = `
        <div class="product-files-display__header">
          <h2 class="product-files-display__product-name" tabindex="-1">${this.escapeHtml(product.title)}</h2>
          ${skuDisplay}
        </div>
      `;

      if (filesCount === 0) {
        contentHTML += `
          <div class="product-files-display__empty" role="status">
            <p>No support files are available for this product yet.</p>
          </div>
        `;
        this.announceToScreenReader(`No support files available for ${product.title}`);
      } else {
        contentHTML += `
          <div class="product-files-display__categories">
            ${this.createFilesHTML(product.supportFiles)}
          </div>
        `;
        
        // Count files by category for announcement
        const categories = this.groupFilesByCategory(product.supportFiles);
        const categoryCount = Object.keys(categories).length;
        this.announceToScreenReader(`Showing ${filesCount} support ${filesCount === 1 ? 'file' : 'files'} for ${product.title} in ${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`);
      }

      // Insert all content at once
      this.filesDisplay.innerHTML = contentHTML;

      // Show the files display
      this.filesDisplay.hidden = false;

      // Scroll to files display and focus on heading
      setTimeout(() => {
        this.filesDisplay.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Move focus to the product name heading
        const heading = this.filesDisplay.querySelector('.product-files-display__product-name');
        if (heading) {
          heading.focus();
        }
      }, 100);
    }

    /**
     * Create HTML for support files organized by category
     */
    createFilesHTML(files) {
      // Group files by category
      const categories = this.groupFilesByCategory(files);
      
      // Category order
      const categoryOrder = [
        'Manuals',
        'Software',
        'Firmware',
        'Whitepapers',
        'Info Sheet',
        'Drawings',
        'Other'
      ];

      const html = categoryOrder
        .filter(category => categories[category] && categories[category].length > 0)
        .map(category => this.createCategoryHTML(category, categories[category]))
        .join('');

      return html;
    }

    /**
     * Group files by category
     */
    groupFilesByCategory(files) {
      const groups = {};
      
      files.forEach(file => {
        const category = file.category || 'Other';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(file);
      });

      return groups;
    }

    /**
     * Create HTML for a file category
     */
    createCategoryHTML(category, files) {
      const icon = this.getCategoryIcon(category);
      
      return `
        <div class="product-files-category">
          <h3 class="product-files-category__heading">
            ${icon}
            <span>${this.escapeHtml(category)}</span>
          </h3>
          <ul class="product-files-category__list">
            ${files.map(file => this.createFileItemHTML(file)).join('')}
          </ul>
        </div>
      `;
    }

    /**
     * Get icon SVG for category
     */
    getCategoryIcon(category) {
      const icons = {
        'Manuals': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>',
        'Software': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>',
        'Firmware': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>',
        'Whitepapers': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>',
        'Info Sheet': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>',
        'Drawings': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 19l7-7 3 3-7 7-3-3z"></path><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path><path d="M2 2l7.586 7.586"></path><circle cx="11" cy="11" r="2"></circle></svg>',
        'Other': '<svg class="product-files-category__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>'
      };

      return icons[category] || icons['Other'];
    }

    /**
     * Create HTML for a file item
     */
    createFileItemHTML(file) {
      const link = this.getFileLink(file);
      const type = this.getFileType(file);
      const hasDescription = file.description && file.description.trim() !== '';
      
      return `
        <li class="product-file-item">
          <div class="product-file-item__header">
            <h4 class="product-file-item__title">${this.escapeHtml(file.title)}</h4>
            ${type ? `<span class="product-file-item__type-badge">${this.escapeHtml(type)}</span>` : ''}
          </div>
          ${hasDescription ? `
            <p class="product-file-item__description">${this.escapeHtml(file.description)}</p>
          ` : ''}
          <div class="product-file-item__actions">
            ${this.createFileActions(file, link)}
          </div>
        </li>
      `;
    }

    /**
     * Get the primary link for a file
     */
    getFileLink(file) {
      // Priority: pageUri > remoteUrl > shopifyFile
      if (file.pageUri) return file.pageUri;
      if (file.remoteUrl) return file.remoteUrl;
      if (file.shopifyFile) return file.shopifyFile;
      return null;
    }

    /**
     * Get file type from URL or category
     */
    getFileType(file) {
      const link = this.getFileLink(file);
      if (!link) return null;

      const ext = link.split('.').pop().toLowerCase();
      const typeMap = {
        'pdf': 'PDF',
        'zip': 'ZIP',
        'exe': 'EXE',
        'dmg': 'DMG',
        'doc': 'DOC',
        'docx': 'DOCX',
        'jpg': 'JPG',
        'jpeg': 'JPEG',
        'png': 'PNG'
      };

      return typeMap[ext] || null;
    }

    /**
     * Create action buttons for a file
     */
    createFileActions(file, link) {
      if (!link) {
        return '<span class="product-file-item__no-link">File not available</span>';
      }

      const isPageLink = file.pageUri && file.pageUri.trim() !== '';
      const downloadIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>';
      const viewIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>';

      if (isPageLink) {
        return `
          <a href="${link}" class="product-file-item__button" target="_blank" rel="noopener noreferrer">
            ${viewIcon}
            View Page
          </a>
        `;
      } else {
        // For files (remote URL or Shopify file), show download button only
        return `
          <a href="${link}" class="product-file-item__button" download>
            ${downloadIcon}
            Download
          </a>
        `;
      }
    }

    /**
     * Display error message
     */
    displayError(message) {
      this.resultsElement.innerHTML = `
        <div class="product-files-search__empty" role="alert">
          <p>${this.escapeHtml(message)}</p>
        </div>
      `;
      this.announceToScreenReader(message);
    }

    /**
     * Show loading indicator
     */
    showLoading() {
      if (this.loadingElement) {
        this.loadingElement.hidden = false;
      }
    }

    /**
     * Hide loading indicator
     */
    hideLoading() {
      if (this.loadingElement) {
        this.loadingElement.hidden = true;
      }
    }

    /**
     * Open predictive results dropdown
     */
    openResults() {
      // Check if there are items to display before opening
      const items = this.resultsElement.querySelectorAll('[role="option"]');
      if (items.length === 0) {
        console.log('Not opening dropdown - no results to display');
        return;
      }
      
      this.predictiveResults.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      
      // Make dropdown items focusable
      items.forEach(item => {
        item.setAttribute('tabindex', '0');
      });
      
      // Also make "Load More" button focusable if present
      const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
      if (loadMoreBtn) {
        loadMoreBtn.setAttribute('tabindex', '0');
      }
      
      // Announce to screen readers
      this.announceToScreenReader(`${items.length} product ${items.length === 1 ? 'suggestion' : 'suggestions'} available. Use arrow keys to navigate.`);
    }

    /**
     * Close predictive results dropdown
     */
    closeResults() {
      this.predictiveResults.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.selectedIndex = -1;
      
      // Remove dropdown items from tab order
      this.resultsElement.querySelectorAll('[role="option"]').forEach(item => {
        item.setAttribute('tabindex', '-1');
      });
      
      // Also remove "Load More" button from tab order if present
      const loadMoreBtn = this.resultsElement.querySelector('.product-files-search__load-more-button');
      if (loadMoreBtn) {
        loadMoreBtn.setAttribute('tabindex', '-1');
      }
      
      // Reset search button label
      if (this.searchButton) {
        this.searchButton.setAttribute('aria-label', 'Search for product support files');
      }
    }

    /**
     * Hide files display
     */
    hideFilesDisplay() {
      this.filesDisplay.hidden = true;
      this.filesDisplay.innerHTML = ''; // Clear content to remove empty heading
      if (this.clearContainer) {
        this.clearContainer.hidden = true;
      }
    }

    /**
     * Announce message to screen readers
     */
    announceToScreenReader(message) {
      let liveRegion = document.getElementById('product-files-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'product-files-live-region';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'visually-hidden';
        document.body.appendChild(liveRegion);
      }
      
      liveRegion.textContent = '';
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 100);
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      if (!text) return '';
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    }
  }

  // Register custom element
  if (!customElements.get('product-files-search')) {
    customElements.define('product-files-search', ProductFilesSearch);
  }
}

